import React, {useEffect, useMemo, useState} from "react";
import {Accordion, Alert, Col, Form, Row} from "react-bootstrap";
import {PiWarningCircle} from "react-icons/pi";
import HelpTooltip from "../HelpTooltip.jsx";
import SelectList from "../SelectList.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {formatNumber} from "./format.js";

const SERVICE_LEVEL_LABELS = {90: "90 %", 95: "95 %", 99: "99 %"};

/**
 * Wandelt einen rohen Eingabewert (Textfeld) in eine gültige, mindestens
 * `min` große Ganzzahl - ungültige Eingaben fallen auf den zuletzt gültigen
 * Wert zurück, statt eine native Formularfehlermeldung auszulösen (siehe
 * globale Vorgabe "keine browsereigenen Bedienelemente").
 */
function sanitizeInt(raw, fallback, min = 0) {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
        return fallback;
    }
    return Math.max(min, parsed);
}

/**
 * Annahmen-Leiste der Lagerstrategie: zeigt die aktuell verwendeten
 * Rechenannahmen dauerhaft sichtbar in der Kopfzeile (auch eingeklappt) und
 * lässt die sechs vom Auftraggeber genannten Annahmen ändern. Jede Änderung
 * wird - nach kurzer Schreibpause, damit nicht bei jedem Tastendruck neu
 * gerechnet wird - über `onChangeParams` an die aufrufende Seite gemeldet;
 * die ruft daraufhin alle Ansichten (Kennzahlen, Handlungslisten, Diagramm,
 * Detailtabelle) mit den neuen Annahmen neu ab.
 *
 * "warehouse_ids" und "demand_window_months" sind zwar über die gleichen
 * Endpunkte änderbar (siehe InventoryStrategyController), bekommen laut
 * Auftrag aber bewusst kein Bedienelement - die Seite bleibt beim
 * Server-Standard (nur SNV Weilheim, 12 Monate Betrachtungszeitraum).
 */
export default function InventoryStrategyAssumptions({onChangeParams}) {
    const {data: defaults, loading, error} = useStatisticsData("/statistics/inventory-strategy/assumptions", {});

    const [leadTimeDosen, setLeadTimeDosen] = useState(null);
    const [leadTimeMarker, setLeadTimeMarker] = useState(null);
    const [leadTimeDefault, setLeadTimeDefault] = useState(null);
    const [serviceLevel, setServiceLevel] = useState(null);
    const [reorderCycleDays, setReorderCycleDays] = useState(null);
    const [unitsPerPallet, setUnitsPerPallet] = useState(null);

    // Eingabefelder (Rohtext) - erst nach kurzer Schreibpause wird daraus ein
    // committed Zahlenwert, der tatsächlich neu abfragt (siehe Suchfeld-
    // Muster in Statistics/Index.jsx).
    const [leadTimeDosenInput, setLeadTimeDosenInput] = useState("");
    const [leadTimeMarkerInput, setLeadTimeMarkerInput] = useState("");
    const [leadTimeDefaultInput, setLeadTimeDefaultInput] = useState("");
    const [reorderCycleDaysInput, setReorderCycleDaysInput] = useState("");
    const [unitsPerPalletInput, setUnitsPerPalletInput] = useState("");

    const seeded = leadTimeDosen !== null;

    // Serverstandard nur EINMAL als Startwert übernehmen - danach bestimmt
    // ausschließlich die Eingabe des Nutzers den Zustand, ein erneuter Abruf
    // dieses Endpunkts (er ändert sich ja nicht) darf laufende Eingaben nicht
    // überschreiben.
    useEffect(() => {
        if (!defaults || seeded) {
            return;
        }
        setLeadTimeDosen(defaults.lead_time_days.dosen);
        setLeadTimeMarker(defaults.lead_time_days.marker);
        setLeadTimeDefault(defaults.lead_time_days.default);
        setServiceLevel(defaults.service_level);
        setReorderCycleDays(defaults.reorder_cycle_days);
        const pallet = Object.values(defaults.units_per_pallet || {})[0] ?? 696;
        setUnitsPerPallet(pallet);

        setLeadTimeDosenInput(String(defaults.lead_time_days.dosen));
        setLeadTimeMarkerInput(String(defaults.lead_time_days.marker));
        setLeadTimeDefaultInput(String(defaults.lead_time_days.default));
        setReorderCycleDaysInput(String(defaults.reorder_cycle_days));
        setUnitsPerPalletInput(String(pallet));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaults, seeded]);

    // Schreibpause: Textfeld-Eingaben erst nach 500 ms als gültigen,
    // committeten Wert übernehmen.
    useEffect(() => {
        if (!seeded) {
            return undefined;
        }
        const timeout = setTimeout(() => setLeadTimeDosen(sanitizeInt(leadTimeDosenInput, leadTimeDosen, 1)), 500);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadTimeDosenInput, seeded]);

    useEffect(() => {
        if (!seeded) {
            return undefined;
        }
        const timeout = setTimeout(() => setLeadTimeMarker(sanitizeInt(leadTimeMarkerInput, leadTimeMarker, 1)), 500);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadTimeMarkerInput, seeded]);

    useEffect(() => {
        if (!seeded) {
            return undefined;
        }
        const timeout = setTimeout(() => setLeadTimeDefault(sanitizeInt(leadTimeDefaultInput, leadTimeDefault, 1)), 500);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadTimeDefaultInput, seeded]);

    useEffect(() => {
        if (!seeded) {
            return undefined;
        }
        const timeout = setTimeout(() => setReorderCycleDays(sanitizeInt(reorderCycleDaysInput, reorderCycleDays, 0)), 500);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reorderCycleDaysInput, seeded]);

    useEffect(() => {
        if (!seeded) {
            return undefined;
        }
        const timeout = setTimeout(() => setUnitsPerPallet(sanitizeInt(unitsPerPalletInput, unitsPerPallet, 1)), 500);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitsPerPalletInput, seeded]);

    const effectiveParams = useMemo(
        () =>
            seeded
                ? {
                      lead_time_dosen: leadTimeDosen,
                      lead_time_marker: leadTimeMarker,
                      lead_time_default: leadTimeDefault,
                      service_level: serviceLevel,
                      reorder_cycle_days: reorderCycleDays,
                      units_per_pallet: unitsPerPallet,
                  }
                : null,
        [seeded, leadTimeDosen, leadTimeMarker, leadTimeDefault, serviceLevel, reorderCycleDays, unitsPerPallet]
    );

    useEffect(() => {
        if (effectiveParams) {
            onChangeParams(effectiveParams);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveParams]);

    const serviceLevelOptions = Object.keys(defaults?.service_level_options || SERVICE_LEVEL_LABELS)
        .map(Number)
        .sort((a, b) => a - b)
        .map((level) => ({
            value: String(level),
            label: `${SERVICE_LEVEL_LABELS[level] || `${level} %`} (z = ${String(defaults?.service_level_options?.[level] ?? "").replace(".", ",")})`,
        }));

    const summaryLine = seeded
        ? `Lieferzeit ${formatNumber(leadTimeDosen)} / ${formatNumber(leadTimeMarker)} / ${formatNumber(leadTimeDefault)} Tage (Dosen/Marker/Übrige) · Lieferfähigkeit ${serviceLevel} % · Bestellzyklus ${formatNumber(reorderCycleDays)} Tage · ${formatNumber(unitsPerPallet)} Stück je Palette`
        : "Rechenannahmen werden geladen…";

    return (
        <div className="d-flex flex-column gap-2">
            <Accordion>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        <div className="d-flex flex-column">
                            <span className="fw-semibold">Rechenannahmen</span>
                            <span className="small text-muted">{summaryLine}</span>
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        {error && <Alert variant="danger">{error}</Alert>}
                        {loading && !seeded ? (
                            <div className="text-muted small">Wird geladen…</div>
                        ) : (
                            <>
                                <Row className="g-3">
                                    <Col md={4} lg={3}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Lieferzeit Dosen
                                            <HelpTooltip placement="right" size={0.7}>
                                                Wiederbeschaffungszeit in Tagen für die Dosen-Warengruppen (SPRA/SPR2/SPR3/SPR4). Standard 49 Tage.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            size="sm"
                                            value={leadTimeDosenInput}
                                            onChange={(event) => setLeadTimeDosenInput(event.target.value)}
                                        />
                                    </Col>
                                    <Col md={4} lg={3}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Lieferzeit Marker
                                            <HelpTooltip placement="right" size={0.7}>
                                                Wiederbeschaffungszeit in Tagen für die Marker-Warengruppen (MARK/MAR2/MAR3). Standard 7 Tage.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            size="sm"
                                            value={leadTimeMarkerInput}
                                            onChange={(event) => setLeadTimeMarkerInput(event.target.value)}
                                        />
                                    </Col>
                                    <Col md={4} lg={3}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Lieferzeit Übrige
                                            <HelpTooltip placement="right" size={0.7}>
                                                Wiederbeschaffungszeit in Tagen für alle übrigen Warengruppen (inkl. Artikel ohne Warengruppe). Standard 28 Tage.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            size="sm"
                                            value={leadTimeDefaultInput}
                                            onChange={(event) => setLeadTimeDefaultInput(event.target.value)}
                                        />
                                    </Col>
                                    <Col md={4} lg={3}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Lieferfähigkeit
                                            <HelpTooltip placement="right" size={0.7}>
                                                Gewünschter Servicegrad - bestimmt den Sicherheitsfaktor z der Sicherheitsbestand-Formel. Standard 95 %.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <SelectList
                                            size="sm"
                                            value={String(serviceLevel ?? "")}
                                            options={serviceLevelOptions}
                                            onChange={(value) => setServiceLevel(Number(value))}
                                            ariaLabel="Lieferfähigkeit"
                                        />
                                    </Col>
                                    <Col md={4} lg={3}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Bestellzyklus
                                            <HelpTooltip placement="right" size={0.7}>
                                                Wie oft nachbestellt wird, in Tagen. Fließt zusätzlich zur Lieferzeit in den empfohlenen Zielbestand ein. Standard 30 Tage.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            size="sm"
                                            value={reorderCycleDaysInput}
                                            onChange={(event) => setReorderCycleDaysInput(event.target.value)}
                                        />
                                    </Col>
                                    <Col md={4} lg={3}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Stück je Palette
                                            <HelpTooltip placement="right" size={0.7}>
                                                Nur für Warengruppen mit bekannter Palettengröße (aktuell die Dosen-Warengruppen). Standard 696 Stück.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            size="sm"
                                            value={unitsPerPalletInput}
                                            onChange={(event) => setUnitsPerPalletInput(event.target.value)}
                                        />
                                    </Col>
                                </Row>
                                {defaults && (
                                    <div className="small text-muted mt-3">
                                        Betrachtungszeitraum Absatz: die letzten {defaults.demand_window_months} Monate bezogen auf den jüngsten
                                        Beleg im Datenbestand. Als „auslisten" gilt ein Artikel ohne jeden Absatz über {defaults.delisting_window_months}{" "}
                                        Monate. Bestand geht ausschließlich aus dem unten genannten Lager ein (nicht änderbar).
                                    </div>
                                )}
                            </>
                        )}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <Alert variant="warning" className="d-flex align-items-start gap-2 mb-0 py-2">
                <PiWarningCircle size={20} className="flex-shrink-0 mt-1" aria-hidden="true" />
                <div className="small">
                    <strong>Zur Bestandsquelle:</strong> Gerechnet wird standardmäßig nur mit dem Lager „SNV Weilheim". Das zweite
                    Lager „Lahr Hauptlager" wird derzeit erst erfasst und enthält Platzhalterwerte - seine Bestände sind nicht
                    belastbar und fließen deshalb nicht in „aktueller Bestand" ein. Die Bestandsspalte der Detailtabelle zeigt die
                    Aufschlüsselung je Lager durch Antippen bzw. beim Überfahren mit der Maus.
                    <br />
                    <strong>Zur Aussagekraft:</strong> Die empfohlenen Mengen beruhen allein auf dem Absatz und sind belastbar. Der
                    Vergleich mit dem heutigen Bestand ist es erst, sobald Lahr vollständig erfasst ist.
                </div>
            </Alert>
        </div>
    );
}
